// # Payment Registration Form Component
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../../../../auth/state';
import SharedHeader from '../../../../../components/SharedHeader';
import DashboardFooter from '../../../../../components/DashboardFooter';
import { collection, getDocs, doc, setDoc } from 'firebase/firestore';
import { db } from '../../../../../firebase/config';
import { 
  FaMoneyBill, 
  FaUserAlt, 
  FaPhoneAlt, 
  FaEnvelope, 
  FaGlobe, 
  FaUserTie,
  FaClock,
  FaChalkboardTeacher,
  FaDollarSign,
  FaStickyNote,
  FaExchangeAlt,
  FaTimes,
  FaArrowsAltH,
  FaCheck,
  FaSort,
  FaPlus,
  FaMinus
} from 'react-icons/fa';
import './register_payment.css';
import { nationalityData } from '../../../../../utils/nationality_data';
import { 
  teamMembers, 
  calculateShares, 
  validateShares, 
  getTeamMemberEmail,
  // # Import the new budget/dividend functions
  isFixedBudgetProduct,
  calculateBudgetDividend,
  handleBudgetDividendChange,
  handleProductChange,
  handleEgpTotalChange,
  handleNationalityChange,
  handleMentorChange
} from './register_pay_calc';
// # Import the PayCustomer component
import PayCustomer from './pay_registry/pay_customer';
// # Import the PayShare component
import PayShare from './pay_registry/pay_share';
// # Import the PayProduct component
import PayProduct from './pay_registry/pay_product';

const RegisterPayment = () => {
  const navigate = useNavigate();
  const { isAdmin, user } = useAuthStore();
  const [users, setUsers] = useState([]);
  const [tutors, setTutors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showConverter, setShowConverter] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');

  // # Currency configuration
  const currencies = [
    // Major Global Currencies
    { code: 'USD', symbol: '$', label: 'US Dollar (USD)' },
    { code: 'EUR', symbol: '€', label: 'Euro (EUR)' },
    { code: 'GBP', symbol: '£', label: 'British Pound (GBP)' },
    { code: 'JPY', symbol: '¥', label: 'Japanese Yen (JPY)' },
    { code: 'CHF', symbol: 'Fr', label: 'Swiss Franc (CHF)' },
    { code: 'CAD', symbol: 'C$', label: 'Canadian Dollar (CAD)' },
    { code: 'AUD', symbol: 'A$', label: 'Australian Dollar (AUD)' },
    
    // Middle East & North Africa
    { code: 'EGP', symbol: 'E£', label: 'Egyptian Pound (EGP)' },
    { code: 'SAR', symbol: 'SR', label: 'Saudi Riyal (SAR)' },
    { code: 'AED', symbol: 'د.إ', label: 'UAE Dirham (AED)' },
    { code: 'KWD', symbol: 'د.ك', label: 'Kuwaiti Dinar (KWD)' },
    { code: 'QAR', symbol: 'ر.ق', label: 'Qatari Riyal (QAR)' },
    { code: 'BHD', symbol: '.د.ب', label: 'Bahraini Dinar (BHD)' },
    { code: 'OMR', symbol: 'ر.ع.', label: 'Omani Rial (OMR)' },
    { code: 'JOD', symbol: 'د.ا', label: 'Jordanian Dinar (JOD)' },
    { code: 'LBP', symbol: 'ل.ل', label: 'Lebanese Pound (LBP)' },
    { code: 'MAD', symbol: 'د.م.', label: 'Moroccan Dirham (MAD)' },
    { code: 'TND', symbol: 'د.ت', label: 'Tunisian Dinar (TND)' },
    { code: 'DZD', symbol: 'د.ج', label: 'Algerian Dinar (DZD)' },
    
    // Asia
    { code: 'CNY', symbol: '¥', label: 'Chinese Yuan (CNY)' },
    { code: 'INR', symbol: '₹', label: 'Indian Rupee (INR)' },
    { code: 'SGD', symbol: 'S$', label: 'Singapore Dollar (SGD)' },
    { code: 'HKD', symbol: 'HK$', label: 'Hong Kong Dollar (HKD)' },
    { code: 'KRW', symbol: '₩', label: 'South Korean Won (KRW)' },
    { code: 'MYR', symbol: 'RM', label: 'Malaysian Ringgit (MYR)' },
    { code: 'THB', symbol: '฿', label: 'Thai Baht (THB)' },
    { code: 'PKR', symbol: '₨', label: 'Pakistani Rupee (PKR)' },
    
    // Europe
    { code: 'SEK', symbol: 'kr', label: 'Swedish Krona (SEK)' },
    { code: 'NOK', symbol: 'kr', label: 'Norwegian Krone (NOK)' },
    { code: 'DKK', symbol: 'kr', label: 'Danish Krone (DKK)' },
    { code: 'PLN', symbol: 'zł', label: 'Polish Złoty (PLN)' },
    { code: 'TRY', symbol: '₺', label: 'Turkish Lira (TRY)' },
    { code: 'RUB', symbol: '₽', label: 'Russian Ruble (RUB)' },
    
    // Americas
    { code: 'BRL', symbol: 'R$', label: 'Brazilian Real (BRL)' },
    { code: 'MXN', symbol: '$', label: 'Mexican Peso (MXN)' },
    { code: 'ARS', symbol: '$', label: 'Argentine Peso (ARS)' },
    { code: 'CLP', symbol: '$', label: 'Chilean Peso (CLP)' },
    { code: 'COP', symbol: '$', label: 'Colombian Peso (COP)' },
    
    // Africa
    { code: 'ZAR', symbol: 'R', label: 'South African Rand (ZAR)' },
    { code: 'NGN', symbol: '₦', label: 'Nigerian Naira (NGN)' },
    { code: 'KES', symbol: 'KSh', label: 'Kenyan Shilling (KES)' },
    { code: 'GHS', symbol: '₵', label: 'Ghanaian Cedi (GHS)' }
  ];

  const [formData, setFormData] = useState({
    type: 'payment',  // # Backend document type
    product: '',      // # Product type (formerly payment type)
    duration: '',
    mentor: '',
    nationality: '',
    userId: '',
    name: '',
    email: '',
    phone: '',
    amount: '',
    currency: 'USD',
    currency_symbol: '$',
    egp_total: '',
    notes: '',
    budget_dvd_percent: 5, // # Default budget dividend percentage
    budget_dvd_amount: '', // # Calculated budget dividend amount in EGP
    net_amount_egp: '',    // # Net amount after budget dividend
    shares: {},           // # Will store share calculations
  });

  // # Product types configuration
  const productTypes = {
    'step1_library': {
      label: 'Step 1 Library Access',
      durations: ['3 months', '6 months', '12 months']
    },
    'step2_library': {
      label: 'Step 2 Library Access',
      durations: ['3 months', '6 months', '12 months']
    },
    'step1_premium': {
      label: 'Step 1 Premium',
      durations: ['1 month']
    },
    'step2_premium': {
      label: 'Step 2 Premium',
      durations: ['1 month']
    },
    'step1_mentorship': {
      label: 'Step 1 Mentorship',
      durations: ['1 month'],
      requiresMentor: true
    },
    'step2_mentorship': {
      label: 'Step 2 Mentorship',
      durations: ['1 month'],
      requiresMentor: true
    },
    'OET': {
      label: 'OET',
      durations: ['1 month'],
      requiresMentor: true
    },
    'step1_tutor_hosting': {
      label: 'Step 1 Tutor Hosting',
      durations: ['1 month'],
      requiresTutor: true
    },
    'step2_tutor_hosting': {
      label: 'Step 2 Tutor Hosting',
      durations: ['1 month'],
      requiresTutor: true
    },
    'US_VISA_Counselling': {
      label: 'US VISA Counselling',
      durations: ['1 month'],
      requiresMentor: true
    }
  };

  // # Fetch users and tutors on component mount
  useEffect(() => {
    const fetchUsersAndTutors = async () => {
      try {
        const usersSnapshot = await getDocs(collection(db, 'users'));
        const usersData = usersSnapshot.docs.map(doc => ({
          uid: doc.id,
          ...doc.data(),
          // Ensure nationality is properly formatted
          nationality: doc.data().nationality || 'Egypt'
        }));

        // # Filter users to get tutors
        const tutorsData = usersData.filter(user => user.role === 'tutor').map(tutor => ({
          id: tutor.uid,
          name: `${tutor.firstName} ${tutor.lastName}`.trim(),
          email: tutor.email
        }));

        setUsers(usersData);
        setTutors(tutorsData);
      } catch (error) {
        console.error('Error fetching users and tutors:', error);
      }
    };

    fetchUsersAndTutors();
  }, []);

  // # Handle currency change
  const handleCurrencyChange = (e) => {
    const selectedCurrency = currencies.find(curr => curr.code === e.target.value);
    setFormData(prev => ({
      ...prev,
      currency: selectedCurrency.code,
      currency_symbol: selectedCurrency.symbol
    }));
  };

  // # Handle budget dividend change
  const handleBudgetDvdChange = (e) => {
    const newPercentage = parseFloat(e.target.value) || 0;
    
    // # Use the function from register_pay_calc.js
    const updatedData = handleBudgetDividendChange(formData, newPercentage);
    
    setFormData(prev => ({
      ...prev,
      ...updatedData
    }));
  };

  // # Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isAdmin) return;
    
    try {
      setLoading(true);
      
      const timestamp = new Date();
      // # Create document ID in year_month format
      const docId = `${timestamp.getFullYear()}_${String(timestamp.getMonth() + 1).padStart(2, '0')}`;
      // # Generate payment ID with ISO timestamp and 10-unit unique identifier
      const uniqueId = Math.random().toString(36).substring(2, 12);
      const paymentId = `payment_${timestamp.toISOString()}_${uniqueId}`;
      
      const paymentData = {
        metadata: {
          created_at: timestamp.toISOString(),
          updated_at: timestamp.toISOString()
        },
        register_admin: {
          uid: user.uid,
          email: user.email,
          name: user.displayName || ''
        },
        transaction_type: 'payment',
        budget_dvd: {
          egp_amount: Number(formData.budget_dvd_amount),
          percentage_of_total: Number(formData.budget_dvd_percent)
        },
        pay: {
          product: formData.product,
          duration: formData.duration,
          amount_native_currency: Number(formData.amount),
          currency: formData.currency,
          currency_symbol: formData.currency_symbol,
          egp_total: Number(formData.egp_total),
          egp_net: Number(formData.egp_total) - Number(formData.budget_dvd_amount)
        },
        user: {
          email: formData.email,
          name: formData.name,
          phone: formData.phone,
          nationality: formData.nationality,
          userId: formData.userId
        },
        shares: formData.shares,
        mentor: {
          mentor_name: "",
          email: "",
          uid: ""
        },
        tutor: {
          tutor_name: "",
          email: "",
          uid: ""
        }
      };

      // # Add mentor if applicable for mentorship products
      if ((formData.product?.includes('mentorship') || formData.product === 'OET' || formData.product === 'US_VISA_Counselling') && formData.mentor) {
        const mentorData = teamMembers.find(m => m.id === formData.mentor);
        if (mentorData) {
          paymentData.mentor = {
            mentor_name: mentorData.name,
            email: mentorData.email,
            uid: mentorData.id
          };
        }
      }

      // # Add tutor if applicable for tutor hosting products
      if (formData.product?.includes('tutor_hosting') && formData.tutor) {
        paymentData.tutor = {
          tutor_name: formData.tutor.name,
          email: formData.tutor.email,
          uid: formData.tutor.id
        };
      }

      const docRef = doc(db, 'financial', docId);
      await setDoc(docRef, {
        payments: {
          [paymentId]: paymentData
        }
      }, { merge: true });

      setShowSuccess(true);
      resetForm();
    } catch (error) {
      console.error('Error registering payment:', error);
      alert('Error registering payment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // # Handle form field changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'currency') {
      handleCurrencyChange(e);
    } else if (name === 'tutor') {
      const selectedTutor = tutors.find(tutor => tutor.id === value);
      if (selectedTutor) {
        setFormData(prev => ({
          ...prev,
          tutor: {
            id: selectedTutor.id,
            name: selectedTutor.name,
            email: selectedTutor.email
          }
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          tutor: null
        }));
      }
    } else if (name === 'product') {  // # Changed from 'type' to 'product'
      // # Use the function from register_pay_calc.js
      const updatedData = handleProductChange(formData, value);
      
      setFormData(prev => ({
        ...prev,
        [name]: value,
        ...updatedData
      }));
    } else if (name === 'phone') {
      // # Format phone number to ensure it has country code
      let formattedPhone = value;
      if (value) {
        // # Remove any non-digit characters except '+'
        formattedPhone = value.replace(/[^\d+]/g, '');
        
        // # Get the selected user's phone number format if available
        const selectedUser = users.find(user => user.uid === formData.userId);
        if (selectedUser?.phoneNumber?.startsWith('+')) {
          // # Extract country code from user's phone number
          const countryCode = selectedUser.phoneNumber.match(/^\+\d+/)[0];
          
          // # If number doesn't start with '+', add user's country code
          if (!formattedPhone.startsWith('+')) {
            if (formattedPhone.startsWith('0')) {
              formattedPhone = countryCode + formattedPhone.substring(1);
            } else {
              formattedPhone = countryCode + formattedPhone;
            }
          }
        } else {
          // # Fallback to Egyptian code if no user selected or no phone format
          if (!formattedPhone.startsWith('+')) {
            if (formattedPhone.startsWith('0')) {
              formattedPhone = '+20' + formattedPhone.substring(1);
            } else if (!formattedPhone.startsWith('20')) {
              formattedPhone = '+20' + formattedPhone;
            }
          }
        }
      }
      
      setFormData(prev => ({
        ...prev,
        [name]: formattedPhone
      }));
    } else if (name === 'egp_total') {
      // # Use the function from register_pay_calc.js
      const updatedData = handleEgpTotalChange(formData, value);
      
      setFormData(prev => ({
        ...prev,
        egp_total: value,
        ...updatedData
      }));
    } else if (name === 'nationality') {
      // # Use the function from register_pay_calc.js
      const updatedData = handleNationalityChange(formData, value);
      
      setFormData(prev => ({
        ...prev,
        [name]: value,
        ...updatedData
      }));
    } else if (name === 'mentor') {
      // # Use the function from register_pay_calc.js
      const updatedData = handleMentorChange(formData, value);
      
      setFormData(prev => ({
        ...prev,
        [name]: value,
        ...updatedData
      }));
    } else if (name === 'userId') {
      const selectedUser = users.find(user => user.uid === value);
      if (selectedUser) {
        // Format phone number when auto-filling
        let formattedPhone = selectedUser.phoneNumber || '';
        if (formattedPhone && !formattedPhone.startsWith('+')) {
          const countryCode = selectedUser.phoneNumber?.match(/^\+\d+/)?.[0] || '+20';
          if (formattedPhone.startsWith('0')) {
            formattedPhone = countryCode + formattedPhone.substring(1);
          } else if (!formattedPhone.startsWith(countryCode.substring(1))) {
            formattedPhone = countryCode + formattedPhone;
          }
        }

        // Get nationality from user data
        let userNationality = selectedUser.nationality || 'EGYPT';
        
        // Find the matching nationality entry by name or search keys
        const nationalityEntry = Object.entries(nationalityData).find(([key, data]) => {
          const userNatLower = userNationality.toLowerCase();
          return data.name.toLowerCase() === userNatLower || 
                 data.searchKeys.some(searchKey => userNatLower.includes(searchKey.toLowerCase()));
        });

        // Use the found nationality name or default to Egypt
        userNationality = nationalityEntry ? nationalityEntry[1].name : nationalityData.EGYPT.name;

        setFormData(prev => ({
          ...prev,
          userId: value,
          name: `${selectedUser.firstName} ${selectedUser.lastName}`,
          email: selectedUser.email,
          phone: formattedPhone,
          nationality: userNationality
        }));
      } else {
        // Clear user-related fields if no user is selected
        setFormData(prev => ({
          ...prev,
          userId: '',
          name: '',
          email: '',
          phone: '',
          nationality: ''
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // # Redirect if not admin
  React.useEffect(() => {
    if (!isAdmin) {
      navigate('/dashboard');
    }
  }, [isAdmin, navigate]);

  // Add filtered and sorted users function
  const getFilteredUsers = () => {
    return users
      .filter(user => {
        const searchTerm = userSearch.toLowerCase();
        const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
        return fullName.includes(searchTerm) || 
               user.email?.toLowerCase().includes(searchTerm);
      })
      .sort((a, b) => {
        const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
        const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
        return sortOrder === 'asc' 
          ? nameA.localeCompare(nameB)
          : nameB.localeCompare(nameA);
      });
  };

  // Add the missing getCurrencySymbol function
  const getCurrencySymbol = (currencyCode) => {
    const currency = currencies.find(c => c.code === currencyCode);
    return currency ? currency.symbol : '$';
  };

  // Add the missing resetForm function
  const resetForm = () => {
    setFormData({
      type: 'payment',
      product: '',
      duration: '',
      mentor: '',
      nationality: '',
      userId: '',
      name: '',
      email: '',
      phone: '',
      amount: '',
      currency: 'USD',
      currency_symbol: '$',
      egp_total: '',
      notes: '',
      budget_dvd_percent: 5,
      budget_dvd_amount: '',
      net_amount_egp: '',
      shares: {}
    });
  };

  return (
    <div className="fnc_pay_reg_page">
      <SharedHeader />
      
      <main className="fnc_pay_reg_content">
        <div className="fnc_pay_reg_header">
          <h1>Register Payment</h1>
          <button 
            className="fnc_pay_reg_back-button"
            onClick={() => navigate('/admin/financial/payments')}
          >
            Back to Payments
          </button>
        </div>

        <form className="fnc_pay_reg_form" onSubmit={handleSubmit}>
          {/* Replace Payment Type Section with PayProduct component */}
          <PayProduct 
            formData={formData}
            handleChange={handleChange}
            productTypes={productTypes}
            teamMembers={teamMembers}
            tutors={tutors}
          />

          {/* Replace Customer Information Section with PayCustomer component */}
          <PayCustomer 
            formData={formData}
            handleChange={handleChange}
            users={users}
            userSearch={userSearch}
            setUserSearch={setUserSearch}
            sortOrder={sortOrder}
            setSortOrder={setSortOrder}
          />

          {/* Payment Amount Section */}
          <div className="fnc_pay_reg_section">
            <div className="fnc_pay_reg_section-header">
              <FaDollarSign />
              <h2>
                Payment Amount
                <FaExchangeAlt 
                  className="fnc_pay_reg_converter-icon"
                  onClick={() => setShowConverter(true)}
                  title="Open Currency Converter"
                />
              </h2>
            </div>

            <div className="fnc_pay_reg_form-row">
              <div className="fnc_pay_reg_form-group">
                <label>
                  <FaDollarSign className="fnc_pay_reg_input-icon" />
                  Currency
                </label>
                <select 
                  name="currency" 
                  value={formData.currency}
                  onChange={handleChange}
                  required
                  className="fnc_pay_reg_currency-select"
                >
                  <optgroup label="Major Global Currencies">
                    {currencies.slice(0, 7).map(curr => (
                      <option key={curr.code} value={curr.code}>
                        {curr.symbol} - {curr.label}
                      </option>
                    ))}
                  </optgroup>
                  
                  <optgroup label="Middle East & North Africa">
                    {currencies.slice(7, 20).map(curr => (
                      <option key={curr.code} value={curr.code}>
                        {curr.symbol} - {curr.label}
                      </option>
                    ))}
                  </optgroup>
                  
                  <optgroup label="Asia">
                    {currencies.slice(20, 28).map(curr => (
                      <option key={curr.code} value={curr.code}>
                        {curr.symbol} - {curr.label}
                      </option>
                    ))}
                  </optgroup>
                  
                  <optgroup label="Europe">
                    {currencies.slice(28, 34).map(curr => (
                      <option key={curr.code} value={curr.code}>
                        {curr.symbol} - {curr.label}
                      </option>
                    ))}
                  </optgroup>
                  
                  <optgroup label="Americas">
                    {currencies.slice(34, 39).map(curr => (
                      <option key={curr.code} value={curr.code}>
                        {curr.symbol} - {curr.label}
                      </option>
                    ))}
                  </optgroup>
                  
                  <optgroup label="Africa">
                    {currencies.slice(39).map(curr => (
                      <option key={curr.code} value={curr.code}>
                        {curr.symbol} - {curr.label}
                      </option>
                    ))}
                  </optgroup>
                </select>
              </div>

              <div className="fnc_pay_reg_form-group">
                <label>
                  <FaDollarSign className="fnc_pay_reg_input-icon" />
                  Amount
                </label>
                <div className="fnc_pay_reg_amount-wrapper">
                  <span className="fnc_pay_reg_currency-symbol">
                    {formData.currency_symbol}
                  </span>
                  <input 
                    type="number"
                    name="amount"
                    value={formData.amount}
                    onChange={handleChange}
                    required
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                  />
                </div>
              </div>
            </div>

            <div className="fnc_pay_reg_form-group">
              <label>
                <FaDollarSign className="fnc_pay_reg_input-icon" />
                EGP Total
              </label>
              <div className="fnc_pay_reg_amount-wrapper">
                <span className="fnc_pay_reg_currency-symbol">E£</span>
                <input 
                  type="number"
                  name="egp_total"
                  value={formData.egp_total}
                  onChange={handleChange}
                  required
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                />
              </div>
            </div>
          </div>

          {/* Additional Notes Section */}
          <div className="fnc_pay_reg_section">
            <div className="fnc_pay_reg_section-header">
              <FaStickyNote />
              <h2>Additional Notes</h2>
            </div>

            <div className="fnc_pay_reg_form-group">
              <label>
                <FaStickyNote className="fnc_pay_reg_input-icon" />
                Notes (Optional)
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows="3"
                placeholder="Add any additional notes here..."
              />
            </div>
          </div>

          {/* Budget Allocation Section */}
          <div className="fnc_pay_reg_section fnc_pay_reg_budget-section">
            <div className="fnc_pay_reg_section-header">
              <FaDollarSign />
              <h2>Budget Allocation</h2>
            </div>

            {/* Check if current product is one of the fixed budget products */}
            {(() => {
              // # Use the imported isFixedBudgetProduct function
              const productHasFixedBudget = isFixedBudgetProduct(formData.product);
              
              return (
                <div className="fnc_pay_reg_budget-controls">
                  <div className="fnc_pay_reg_form-group">
                    <label>
                      <FaDollarSign className="fnc_pay_reg_input-icon" />
                      Budget Dividend (%)
                      {productHasFixedBudget && (
                        <span className="fnc_pay_reg_fixed-budget-note"> (Fixed at 5% for this product)</span>
                      )}
                    </label>
                    <div className="fnc_pay_reg_number-control">
                      <button 
                        type="button"
                        onClick={() => handleBudgetDvdChange({ 
                          target: { value: Math.max(0, formData.budget_dvd_percent - 1) }
                        })}
                        className="fnc_pay_reg_number-btn"
                        disabled={productHasFixedBudget}
                      >
                        -
                      </button>
                      <input
                        type="number"
                        name="budget_dvd_percent"
                        value={formData.budget_dvd_percent}
                        onChange={handleBudgetDvdChange}
                        required
                        min="0"
                        max="100"
                        step="1"
                        disabled={productHasFixedBudget}
                        className={productHasFixedBudget ? "fnc_pay_reg_input-disabled" : ""}
                      />
                      <button 
                        type="button"
                        onClick={() => handleBudgetDvdChange({ 
                          target: { value: Math.min(100, formData.budget_dvd_percent + 1) }
                        })}
                        className="fnc_pay_reg_number-btn"
                        disabled={productHasFixedBudget}
                      >
                        +
                      </button>
                    </div>
                  </div>

                  <div className="fnc_pay_reg_form-group">
                    <label>
                      <FaDollarSign className="fnc_pay_reg_input-icon" />
                      Budget Amount (EGP)
                    </label>
                    <input
                      type="text"
                      value={formData.budget_dvd_amount}
                      readOnly
                      className="fnc_pay_reg_input-readonly"
                    />
                  </div>
                  
                  <div className="fnc_pay_reg_form-group">
                    <label>
                      <FaDollarSign className="fnc_pay_reg_input-icon" />
                      Net Amount (EGP)
                    </label>
                    <input
                      type="text"
                      value={formData.net_amount_egp}
                      readOnly
                      className="fnc_pay_reg_input-readonly"
                    />
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Replace the old dividend preview table with the new component */}
          <PayShare 
            shares={formData.shares}
            budgetDvdAmount={formData.budget_dvd_amount}
            budgetDvdPercent={formData.budget_dvd_percent}
            egpTotal={formData.egp_total}
          />

          {showSuccess && (
            <div className="fnc_pay_reg_success-message">
              <FaCheck className="fnc_pay_reg_success-icon" />
              <span>Payment registered successfully!</span>
              <button 
                type="button"
                onClick={() => setShowSuccess(false)}
                className="fnc_pay_reg_success-close"
              >
                <FaTimes />
              </button>
            </div>
          )}

          {/* Submit Button */}
          <button 
            type="submit" 
            className="fnc_pay_reg_submit-button"
            disabled={loading}
          >
            {loading ? 'Registering...' : 'Register Payment'}
          </button>
        </form>
      </main>

      {/* Currency Converter Popup */}
      {showConverter && (
        <div className="fnc_pay_reg_popup-overlay">
          <div className="fnc_pay_reg_popup-content">
            <button 
              className="fnc_pay_reg_popup-close"
              onClick={() => setShowConverter(false)}
            >
              <FaTimes />
            </button>
            <iframe
              src="https://www.xe.com/currencyconverter/"
              className="fnc_pay_reg_popup-iframe"
              title="Currency Converter"
              target="_blank"
              rel="noopener noreferrer"
            />
          </div>
        </div>
      )}

      <DashboardFooter />
    </div>
  );
};

export default RegisterPayment; 